import { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { getSocket } from '../../../core/services/socket/socket';
import { normalizeTrainingStatus } from '../utils/normalizeTrainingStatus';

// Debug flag - set to true to trace Socket.IO events and state changes
const DEBUG_TRAINING_PROGRESS = false;

// Training progress data interfaces
export interface TrainingProgressData {
    timestamp: string;
    session_id: string;
    status: 'training_starting' | 'training_started' | 'preparing_data' | 'data_transformation' | 'training_completed' | 'model_training_completed' | 'training_failed' | 'post_training' | 'evaluating' | 'uploading_results' | 'results_uploaded' | 'database_saved' | 'uploading_models' | 'models_uploaded';
    message: string;
    progress_percent: number;
    phase: 'initialization' | 'data_preparation' | 'data_transformation' | 'training_execution' | 'post_training' | 'completed' | 'error';
    models_to_train?: string[];
    training_duration?: number;
    models_trained?: number;
    best_model?: unknown;
    estimated_duration?: string;
    // ETA and timing fields from backend
    eta_seconds?: number;
    elapsed_seconds?: number;
    estimated_completion?: number;
    current_iteration?: number;
    total_iterations?: number;
    // Post-training step tracking
    step?: 'evaluating' | 'preparing_save' | 'uploading_results' | 'preparing_upload' | 'compressing' | 'compression_done' | 'uploading' | 'upload_complete' | 'results_uploaded' | 'database_saved' | 'saving_plots' | 'uploading_models' | 'models_uploaded';
}

export interface TrainingMetricsData {
    timestamp: string;
    session_id: string;
    metrics_type: 'real_time';
    epoch?: number;
    total_epochs?: number;
    loss?: number;
    accuracy?: number;
    val_loss?: number;
    val_accuracy?: number;
    model_name?: string;
    eta_seconds?: number;
    eta_formatted?: string;
    progress_percent?: number;
}

export interface TrainingErrorData {
    timestamp: string;
    session_id: string;
    error_type: 'training_error';
    message: string;
    error_details: string;
    phase: string;
    progress_percent: number;
}

// Batch progress data from training_batch_progress event
export interface BatchProgressData {
    batch: number;
    total_batches: number;
    loss: number;
    root_mean_squared_error: number;
    current_epoch: number;
    total_epochs: number;
    model_name: string;
}

export interface TrainingStatus {
    isConnected: boolean;
    currentProgress: TrainingProgressData | null;
    latestMetrics: TrainingMetricsData | null;
    lastError: TrainingErrorData | null;
    connectionError: string | null;
    sessionJoined: boolean;
    batchProgress: BatchProgressData | null;
}

/**
 * Custom hook for managing real-time training progress via SocketIO
 */
export const useTrainingProgress = (sessionId: string | null) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [status, setStatus] = useState<TrainingStatus>({
        isConnected: false,
        currentProgress: null,
        latestMetrics: null,
        lastError: null,
        connectionError: null,
        sessionJoined: false,
        batchProgress: null
    });

    // Use global socket connection
    useEffect(() => {
        if (!sessionId) return;

        const globalSocket = getSocket();

        // Handler functions for cleanup
        const handleConnect = () => {
            if (DEBUG_TRAINING_PROGRESS) {
                console.debug('[useTrainingProgress] 🔌 Socket connected, joining session:', sessionId);
            }
            setStatus(prev => ({ ...prev, isConnected: true, connectionError: null }));
            globalSocket.emit('join_training_session', { session_id: sessionId });
        };

        const handleDisconnect = () => {
            if (DEBUG_TRAINING_PROGRESS) {
                console.debug('[useTrainingProgress] 🔌 Socket disconnected');
            }
            setStatus(prev => ({ ...prev, isConnected: false, sessionJoined: false }));
        };

        const handleConnectError = (error: Error) => {
            console.error('❌ SocketIO connection error:', error);
            setStatus(prev => ({ ...prev, connectionError: error.message }));
        };

        const handleSessionJoined = () => {
            if (DEBUG_TRAINING_PROGRESS) {
                console.debug('[useTrainingProgress] ✅ Joined training session:', sessionId);
            }
            setStatus(prev => ({ ...prev, sessionJoined: true }));
        };

        const handleSessionError = (data: { message: string }) => {
            console.error('❌ Training session error:', data);
            setStatus(prev => ({ ...prev, connectionError: data.message }));
        };

        const handleTrainingProgress = (data: TrainingProgressData) => {
            if (DEBUG_TRAINING_PROGRESS) {
                console.debug('[useTrainingProgress] 📊 training_progress event:', {
                    status: data.status,
                    phase: data.phase,
                    progress: data.progress_percent,
                    step: data.step,
                    message: data.message?.substring(0, 80)
                });
            }
            setStatus(prev => ({ ...prev, currentProgress: data }));
        };

        const handleTrainingMetrics = (data: TrainingMetricsData) => {
            if (DEBUG_TRAINING_PROGRESS) {
                console.debug('[useTrainingProgress] 📈 training_metrics event:', {
                    epoch: data.epoch,
                    totalEpochs: data.total_epochs,
                    progress: data.progress_percent,
                    loss: data.loss?.toFixed(6),
                    valLoss: data.val_loss?.toFixed(6)
                });
            }
            setStatus(prev => ({ ...prev, latestMetrics: data }));
        };

        const handleTrainingError = (data: TrainingErrorData) => {
            console.error('🚨 Training error:', data);
            setStatus(prev => ({ ...prev, lastError: data }));
        };

        const handleStatusUpdate = (data: { session_id: string; status: string; message: string; models_trained?: number; progress_percent?: number }) => {
            if (DEBUG_TRAINING_PROGRESS) {
                console.debug('[useTrainingProgress] 📢 training_status_update event:', {
                    status: data.status,
                    progress: data.progress_percent,
                    message: data.message?.substring(0, 80)
                });
            }
            // Ignore 'not_found' and 'unknown' statuses - they shouldn't activate overlay
            if (data.status === 'not_found' || data.status === 'unknown') {
                if (DEBUG_TRAINING_PROGRESS) {
                    console.debug('[useTrainingProgress] Ignoring non-active status:', data.status);
                }
                return;
            }
            // This recovery path receives RAW unprefixed backend statuses
            // ('completed'/'failed'/'failed_storage'), so normalize first
            // instead of comparing against prefixed legacy strings that never
            // match (which would leave the bar stuck on a real failure).
            const state = normalizeTrainingStatus(data.status);
            setStatus(prev => {
                const lastKnown = prev.currentProgress?.progress_percent ?? 0;
                const progressData: TrainingProgressData = {
                    timestamp: new Date().toISOString(),
                    session_id: data.session_id,
                    // Canonicalize so isTrainingActive recognizes terminal states.
                    status: (state === 'completed' ? 'training_completed' :
                             state === 'failed' ? 'training_failed' :
                             data.status) as TrainingProgressData['status'],
                    message: data.message,
                    // Use backend progress; if absent, keep the last-known value
                    // (never snap to a fixed 50%, which can jump the bar backward).
                    progress_percent: data.progress_percent ??
                        (state === 'completed' ? 100 :
                         state === 'failed' ? 0 : lastKnown),
                    phase: state === 'completed' ? 'completed' :
                           state === 'failed' ? 'error' : 'training_execution',
                    models_trained: data.models_trained,
                };
                return { ...prev, currentProgress: progressData };
            });
        };

        const handleBatchProgress = (data: BatchProgressData) => {
            // Only log occasionally to avoid spam (every 50 batches)
            if (DEBUG_TRAINING_PROGRESS && data.batch % 50 === 0) {
                console.debug('[useTrainingProgress] 📦 training_batch_progress:', {
                    batch: `${data.batch}/${data.total_batches}`,
                    epoch: `${data.current_epoch}/${data.total_epochs}`,
                    loss: data.loss?.toFixed(6)
                });
            }
            setStatus(prev => ({ ...prev, batchProgress: data }));
        };

        // Register event listeners
        globalSocket.on('connect', handleConnect);
        globalSocket.on('disconnect', handleDisconnect);
        globalSocket.on('connect_error', handleConnectError);
        globalSocket.on('training_session_joined', handleSessionJoined);
        globalSocket.on('training_session_error', handleSessionError);
        globalSocket.on('training_progress', handleTrainingProgress);
        globalSocket.on('training_metrics', handleTrainingMetrics);
        globalSocket.on('training_error', handleTrainingError);
        globalSocket.on('training_status_update', handleStatusUpdate);
        globalSocket.on('training_batch_progress', handleBatchProgress);

        // If already connected, join session immediately
        if (globalSocket.connected) {
            setStatus(prev => ({ ...prev, isConnected: true }));
            globalSocket.emit('join_training_session', { session_id: sessionId });
        }

        setSocket(globalSocket);

        // Cleanup - only remove listeners, don't disconnect global socket
        return () => {
            globalSocket.off('connect', handleConnect);
            globalSocket.off('disconnect', handleDisconnect);
            globalSocket.off('connect_error', handleConnectError);
            globalSocket.off('training_session_joined', handleSessionJoined);
            globalSocket.off('training_session_error', handleSessionError);
            globalSocket.off('training_progress', handleTrainingProgress);
            globalSocket.off('training_metrics', handleTrainingMetrics);
            globalSocket.off('training_error', handleTrainingError);
            globalSocket.off('training_status_update', handleStatusUpdate);
            globalSocket.off('training_batch_progress', handleBatchProgress);
        };
    }, [sessionId]);

    // Request current training status
    const requestStatus = useCallback(() => {
        if (socket && sessionId) {
            socket.emit('request_training_status', { session_id: sessionId });
        }
    }, [socket, sessionId]);

    // Clear error state
    const clearError = useCallback(() => {
        setStatus(prev => ({ ...prev, lastError: null, connectionError: null }));
    }, []);

    // Reset all progress state (for new session)
    const resetProgress = useCallback(() => {
        setStatus(prev => ({
            ...prev,
            currentProgress: null,
            latestMetrics: null,
            lastError: null,
            connectionError: null,
            sessionJoined: false,
            batchProgress: null
        }));
    }, []);

    // Get formatted progress percentage
    const getProgressPercentage = useCallback((): number => {
        return status.currentProgress?.progress_percent || 0;
    }, [status.currentProgress]);

    // Get current phase display name
    const getCurrentPhase = useCallback((): string => {
        if (!status.currentProgress) return 'Not started';

        const phaseNames: Record<string, string> = {
            'initialization': 'Initializing training',
            'data_preparation': 'Preparing data',
            'data_transformation': 'Transforming data',
            'training_execution': 'Training models',
            'post_training': 'Saving results',
            'completed': 'Training completed',
            'error': 'Training failed'
        };

        return phaseNames[status.currentProgress.phase] || status.currentProgress.phase;
    }, [status.currentProgress]);

    // Check if training is active
    const isTrainingActive = useCallback((): boolean => {
        if (!status.currentProgress) return false;
        const state = normalizeTrainingStatus(status.currentProgress.status);
        return state !== 'completed' && state !== 'failed';
    }, [status.currentProgress]);

    return {
        ...status,
        requestStatus,
        clearError,
        resetProgress,
        getProgressPercentage,
        getCurrentPhase,
        isTrainingActive,
        // Computed properties
        hasActiveTraining: isTrainingActive(),
        progressPercentage: getProgressPercentage(),
        currentPhase: getCurrentPhase()
    };
};